import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  SavePhoneDialogModel,
  SavePhoneRequest,
} from '../../../interfaces/dialogs/save-phone-dialog.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatError, MatInput } from '@angular/material/input';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { RegisterVerifyContactService } from '../../../services/register-verify-contact.service';
import { API_PARAMS } from '../../../constants/api.constants';
import { SavePhoneDialogService } from '../../../services/save-phone-dialog.service';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { NumericOnlyDirective } from '../../../../directives/numeric-only.directive';

/**
 * 會員中心新增/修改手機dialog組件
 */
@Component({
  selector: 'app-save-phone-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    MatInput,
    FormsModule,
    MatButton,
    ReactiveFormsModule,
    MatError,
    NgClass,
    NgOptimizedImage,
    NumericOnlyDirective,
  ],
  templateUrl: './save-phone-dialog.component.html',
  styleUrl: './save-phone-dialog.component.scss',
})
export class SavePhoneDialogComponent {
  registerVerifyContactService: RegisterVerifyContactService = inject(
    RegisterVerifyContactService
  );
  translateService: TranslateService = inject(TranslateService);
  savePhoneDialogService: SavePhoneDialogService = inject(
    SavePhoneDialogService
  );
  toastr: ToastrService = inject(ToastrService);
  phoneControl = new FormControl('', [
    Validators.required,
    (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      
      // 必須是10位數字且以09開頭
      const phoneRegex = /^09\d{8}$/;
      if (!phoneRegex.test(value)) {
        return {
          invalidFormat: this.translateService.instant('error.contact-error.format')
        };
      }
      return null;
    }
  ]); // 手機號碼
  otpControl = new FormControl('', [
    Validators.required,
    (control: FormControl) => this.otpValidator(control),
  ]); // otp驗證碼

  showPhoneError: boolean = false; // 是否顯示錯誤訊息
  otpRemainSeconds: number = 30; // otp驗證碼時間
  isSendOtpAvailable: boolean = true; // 是否可以發送otp
  showOtpError: boolean = false; // 是否顯示otp錯誤訊息
  isCreateMode: boolean = false; // 是否為新增模式
  isOtpReachedLimit: boolean = false; // 是否達到otp上限

  constructor(
    public dialogRef: MatDialogRef<SavePhoneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SavePhoneDialogModel
  ) {
    // 根據data.isCreate判斷是新增還是修改模式
    this.isCreateMode = this.data.isCreate;
  }

  // 根據標題判斷是否為新增模式
  private determineCreateMode(): boolean {
    // 直接使用data.isCreate屬性
    return this.data.isCreate;
  }

  // 手機號碼驗證器
  phoneValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (value) {
      const phoneRegex = /^09.{8}$/.test(value); // 手機號碼為10位數字
      if (!phoneRegex) {
        return {
          invalidFormat: this.translateService.instant(
            'error.phone-error.format'
          ),
        };
      }
    }
    return null;
  }

  // otp驗證碼驗證器
  otpValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    // 驗證只能輸入6位數字
    if (value && !/^\d{6}$/.test(value)) {
      return {
        invalidFormat: this.translateService.instant('error.otp-error.format'),
      };
    }
    return null;
  }

  // 獲取手機號碼錯誤訊息
  getPhoneErrorMessage() {
    const control = this.phoneControl;
    if (!control || !control.errors || !this.showPhoneError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.phone-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 獲取otp錯誤訊息
  getOtpErrorMessage() {
    const control = this.otpControl;
    if (!control || !control.errors || !this.showOtpError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.otp-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 發送驗證碼
  sendOtp() {
    this.showPhoneError = true;
    if (this.phoneControl.valid) {
      this.otpCountDown();
      this.registerVerifyContactService
        .registerSendOtp(
          this.data.idNo,
          API_PARAMS.PHONE,
          this.phoneControl.value ?? ''
        )
        .subscribe({
          next: (res) => {
            // 當驗證碼請求次數過多時，請於 15 分鐘後再試
            if (res.statusCode === 'EC5004') {
              this.showOtpError = false;
              this.isOtpReachedLimit = true;
            }
          },
        });
    }
  }

  // otp倒數
  otpCountDown() {
    this.isSendOtpAvailable = false;
    const interval = setInterval(() => {
      this.otpRemainSeconds--;
      if (this.otpRemainSeconds == 0) {
        clearInterval(interval);
        this.otpRemainSeconds = 30;
        this.isSendOtpAvailable = true;
      }
    }, 1000);
  }

  // 新增/修改手機號碼
  save() {
    this.showOtpError = true;
    if (this.otpControl.valid) {
      const request: SavePhoneRequest = {
        idNo: this.data.idNo,
        chartNo: this.data.chartNo,
        cell: this.phoneControl.value ?? '',
        otp: this.otpControl.value ?? '',
      };

      this.savePhoneDialogService
        .savePhone(request)
        .subscribe({
          next: (res) => {
            if (res.statusCode === 'S0000') {
              this.toastr.success(
                this.translateService.instant(
                  this.isCreateMode
                    ? 'save-phone-dialog.create-success'
                    : 'save-phone-dialog.modify-success'
                )
              );
              this.dialogRef.close(res.data?.cell);
            } else {
              // 其他錯誤情況
              this.toastr.error(
                this.translateService.instant('save-phone-dialog.modify-error')
              );
              this.dialogRef.close(true);
            }
          },
          error: (error) => {
            console.error('新增/修改手機號碼失敗:', error);
            this.toastr.error(
              this.translateService.instant('save-phone-dialog.modify-error')
            );
            this.dialogRef.close();
          }
        });
    }
  }

  // 關閉dialog
  closeDialog() {
    this.dialogRef.close();
  }

  // 稍後填寫（必填手機情境下的關閉），回傳標記讓呼叫端跳提示
  laterFill() {
    this.dialogRef.close('later');
  }
}
